import { useEffect, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8081/api';

export interface SSEEvent {
  type: 'announcement' | 'notification' | 'unread-changed' | 'heartbeat';
  data: unknown;
}

interface UseSSEOptions {
  onEvent: (event: SSEEvent) => void;
  onReconnect?: () => void;
  enabled?: boolean;
}

/**
 * SSE 连接 hook，自动重连 + 指数退避
 */
export function useSSE({ onEvent, onReconnect, enabled = true }: UseSSEOptions) {
  const eventSourceRef = useRef<EventSource | null>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const hasConnectedRef = useRef(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // 关闭旧连接
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // EventSource 不支持自定义 Header，只能通过 query param 传 JWT。
    // 已知 trade-off：token 会出现在服务器访问日志中。
    // 后续可优化为短期一次性 SSE ticket 机制。
    const es = new EventSource(`${API_BASE}/sse/stream?token=${token}`);

    es.addEventListener('announcement', (e: MessageEvent) => {
      try {
        onEventRef.current({ type: 'announcement', data: JSON.parse(e.data) });
      } catch {
        console.warn('[SSE] Failed to parse announcement event');
      }
    });

    es.addEventListener('notification', (e: MessageEvent) => {
      try {
        onEventRef.current({ type: 'notification', data: JSON.parse(e.data) });
      } catch {
        console.warn('[SSE] Failed to parse notification event');
      }
    });

    es.addEventListener('unread-changed', (e: MessageEvent) => {
      try {
        onEventRef.current({ type: 'unread-changed', data: JSON.parse(e.data) });
      } catch {
        console.warn('[SSE] Failed to parse unread-changed event');
      }
    });

    es.addEventListener('heartbeat', () => {
      // 连接保活，重置重试计数
      retryCountRef.current = 0;
    });

    es.onopen = () => {
      const wasReconnect = hasConnectedRef.current;
      hasConnectedRef.current = true;
      retryCountRef.current = 0;

      // On reconnect, notify caller to refresh stale data
      // (events may have been lost during the disconnect window)
      if (wasReconnect && onReconnectRef.current) {
        onReconnectRef.current();
      }
    };

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;

      // 指数退避重连：3s, 6s, 12s, 最大 30s
      const delay = Math.min(3000 * Math.pow(2, retryCountRef.current), 30000);
      retryCountRef.current++;

      retryTimerRef.current = setTimeout(connect, delay);
    };

    eventSourceRef.current = es;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    connect();

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [connect, enabled]);
}
